import { Config, Cron } from '@langchain/langgraph-sdk';

import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';

import { useLangGraphClient } from '@/Pages/Graph/src/api';
import useToast from '@/components/Toast/useToast';
import { FetcherParams, fetcher } from '@/data/fetcher';

export type CronSortBy =
  | 'created_at'
  | 'cron_id'
  | 'updated_at'
  | 'assistant_id'
  | 'thread_id'
  | 'next_run_date';
export type CronSortOrder = 'desc' | 'asc';

type CronSearchResponse = Cron[];

type CronSearchRequest = {
  limit?: number;
  offset?: number;
  assistant_id?: string;
  thread_id?: string;
  sort_by?: CronSortBy;
  sort_order?: CronSortOrder;
};

type CronCreateRequest = {
  schedule: string;
  assistant_id: string;
  input?: Record<string, any>;
  metadata?: Record<string, any>;
  config?: Config;
  end_time?: string;
};

type CronCreateRequestForThread = CronCreateRequest & {
  thread_id: string;
};

export const useCrons = (params: CronSearchRequest) => {
  const client = useLangGraphClient();
  const [_, apiUrl, authedFetch] = client;
  return useSWR([client, 'crons', params], async ([, , params]) => {
    const res = await fetcher<CronSearchResponse, CronSearchRequest>(
      {
        url: `${apiUrl}/runs/crons/search`,
        method: 'POST',
        body: JSON.stringify(params),
      },
      undefined,
      authedFetch
    );
    const total = res.headers?.['x-pagination-total']
      ? parseInt(res.headers['x-pagination-total'], 10)
      : 0;
    return {
      crons: res,
      total,
    };
  });
};

export const useCreateCron = () => {
  const { createToast } = useToast();
  const client = useLangGraphClient();
  const [_, apiUrl, authedFetch] = client;
  return useSWRMutation<
    Cron,
    Error,
    FetcherParams<never, never>,
    CronCreateRequest
  >(
    {
      url: `${apiUrl}/runs/crons`,
      method: 'POST',
    },
    (key, { arg }) => {
      return fetcher<Cron, CronCreateRequest>(
        {
          url: key.url,
          method: key.method,
          body: JSON.stringify(arg),
        },
        undefined,
        authedFetch
      );
    },
    {
      onSuccess: () => {
        mutate((key) => {
          return Array.isArray(key) && key[0] === client && key[1] === 'crons';
        });
      },
      onError: (error) => {
        createToast({
          title: 'Failed to create cron',
          description: error.message,
          type: 'error',
        });
      },
    }
  );
};

export const useCreateCronForThread = ({ threadId }: { threadId: string }) => {
  const { createToast } = useToast();
  const client = useLangGraphClient();
  const [_, apiUrl, authedFetch] = client;
  return useSWRMutation<
    Cron,
    Error,
    FetcherParams<never, never>,
    CronCreateRequestForThread
  >(
    {
      url: `${apiUrl}/threads/${threadId}/runs/crons`,
      method: 'POST',
    },
    (key, { arg }) => {
      return fetcher<Cron, CronCreateRequest>(
        {
          url: key.url,
          method: key.method,
          body: JSON.stringify(arg),
        },
        undefined,
        authedFetch
      );
    },
    {
      onSuccess: () => {
        mutate((key) => {
          return Array.isArray(key) && key[0] === client && key[1] === 'crons';
        });
      },
      onError: (error) => {
        createToast({
          title: 'Failed to create cron',
          description: error.message,
          type: 'error',
        });
      },
    }
  );
};

export const useDeleteCron = () => {
  const { createToast } = useToast();
  const client = useLangGraphClient();
  const [_, apiUrl, authedFetch] = client;
  return useSWRMutation<
    Cron,
    never,
    FetcherParams<never, never>,
    { cronId: string }
  >(
    {
      url: `${apiUrl}/runs/crons`,
      method: 'DELETE',
    },
    (key, { arg }) => {
      return fetcher<Cron, CronCreateRequest>(
        {
          url: `${key.url}/${arg.cronId}`,
          method: key.method,
          body: JSON.stringify(arg),
        },
        undefined,
        authedFetch
      );
    },
    {
      onSuccess: () => {
        mutate((key) => {
          return Array.isArray(key) && key[0] === client && key[1] === 'crons';
        });
      },
      onError: () => {
        createToast({
          title: 'Failed to delete cron',
          type: 'error',
        });
      },
    }
  );
};
